import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const RegisterForm = ({ currentLanguage }) => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    role: 'buyer'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const translations = {
    en: {
      title: 'Create Account',
      subtitle: 'Join OGADA marketplace today',
      fullName: 'Full Name',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      role: 'I want to',
      buyer: 'Buy items (Buyer)',
      traveler: 'Deliver items (Traveler)',
      signUp: 'Sign Up',
      passwordMismatch: 'Passwords do not match'
    },
    ko: {
      title: '계정 만들기',
      subtitle: '오늘 OGADA 마켓플레이스에 가입하세요',
      fullName: '성명',
      email: '이메일',
      password: '비밀번호',
      confirmPassword: '비밀번호 확인',
      role: '역할 선택',
      buyer: '아이템 구매 (구매자)',
      traveler: '아이템 배송 (여행자)',
      signUp: '회원가입',
      passwordMismatch: '비밀번호가 일치하지 않습니다'
    }
  };

  const t = translations?.[currentLanguage];

  const roleOptions = [
    { value: 'buyer', label: t?.buyer },
    { value: 'traveler', label: t?.traveler }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (formData?.password !== formData?.confirmPassword) {
      setError(t?.passwordMismatch);
      return;
    }

    setLoading(true);

    try {
      const { error } = await signUp(formData?.email, formData?.password, {
        full_name: formData?.fullName,
        role: formData?.role
      });

      if (error) {
        setError(error?.message);
      } else {
        navigate('/unified-dashboard');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-lg p-6 md:p-8">
      <div className="mb-6">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
          {t?.title}
        </h2>
        <p className="text-sm text-muted-foreground">{t?.subtitle}</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
          <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={t?.fullName}
          type="text"
          value={formData?.fullName}
          onChange={(e) => setFormData({ ...formData, fullName: e?.target?.value })}
          placeholder="John Doe"
          required
        />

        <Input
          label={t?.email}
          type="email"
          value={formData?.email}
          onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
          placeholder="you@example.com"
          required
        />

        <Input
          label={t?.password}
          type="password"
          value={formData?.password}
          onChange={(e) => setFormData({ ...formData, password: e?.target?.value })}
          placeholder="••••••••"
          required
        />

        <Input
          label={t?.confirmPassword}
          type="password"
          value={formData?.confirmPassword}
          onChange={(e) => setFormData({ ...formData, confirmPassword: e?.target?.value })}
          placeholder="••••••••"
          required
        />

        <Select
          label={t?.role}
          options={roleOptions}
          value={formData?.role}
          onChange={(value) => setFormData({ ...formData, role: value })}
        />

        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={loading}
          iconName="UserPlus"
          iconPosition="left"
        >
          {t?.signUp}
        </Button>
      </form>
    </div>
  );
};

export default RegisterForm;